/**
 * How many decisions are waiting for this reader **in the scope on screen** — one rule, two readers.
 *
 * The sidebar badge and the browser tab both show this number (I-0611, AC-5). They are in different
 * components, they re-render on different things, and the only way they cannot disagree is to ask
 * one function rather than to each restate the same three-line test against `DecisionsStore`.
 *
 * ⛔ **The scope is checked, not assumed.** `DecisionsStore` holds one set at a time and the set it
 * holds belongs to whatever was last asked for; while the reader is moving between work areas that
 * is briefly the *previous* one. A number taken without checking is the old work area's count under
 * the new work area's name, which is a worse answer than none — so a set that is not about the scope
 * on screen reads as "not known yet", and the badge shows `...` rather than a number that is wrong.
 */

/** What `DecisionsStore` has to be able to answer for this rule; the store itself satisfies it. */
export interface IDecisionsSetSource {
  /** The current set spans every work area this reader may read. */
  allWorkAreas: boolean;
  /** The work area the current set belongs to, or `null` under All work areas / before any read. */
  workAreaId: string | null;
  /** The namespace the current set is scoped to (F-0383, §10.9), or `null` outside that scope. */
  namespaceId?: string | null;
  /** The size of the current set, `undefined` while the first read for it is in flight. */
  waitingCount: number | undefined;
  /** The first read failed and nothing is known. */
  unknown: boolean;
}

/** The scope the screen is showing, which the set has to be about for its number to mean anything. */
export interface IDecisionsScope {
  /** Every work area at once (I-0405) — see `isAllWorkAreasScope`. */
  allWorkAreas: boolean;
  /** The one work area on screen; `null` under All work areas, or before one is known. */
  workAreaId: string | null;
  /** The namespace on screen (F-0383, §10.9), or `null`/absent outside that scope. */
  namespaceId?: string | null;
}

/**
 * Whether the set the store is holding is the set this screen is about.
 *
 * ⛔ `workAreaId === scope.workAreaId` alone is not enough: both are `null` under All work areas and
 * before the first read, so a narrow scope with no work area yet would adopt the All-work-areas
 * count. A narrow scope with no work area is a scope whose set does not exist. A namespace scope is
 * its own third case for the same reason — a namespace and a single work area are both represented
 * as "not All work areas", so the namespace id has to be checked rather than assumed from that.
 */
export function decisionsSetMatchesScope(
  decisions: IDecisionsSetSource,
  scope: IDecisionsScope,
): boolean {
  if (scope.allWorkAreas) return decisions.allWorkAreas;
  if (scope.namespaceId) return decisions.namespaceId === scope.namespaceId;
  return scope.workAreaId !== null && decisions.workAreaId === scope.workAreaId;
}

/** The number to show, or `undefined` when it is not known for this scope yet. */
export function decisionsWaitingInScope(
  decisions: IDecisionsSetSource,
  scope: IDecisionsScope,
): number | undefined {
  return decisionsSetMatchesScope(decisions, scope) ? decisions.waitingCount : undefined;
}

/** The read for this scope failed: say nothing, rather than `...` for a number never coming. */
export function decisionsWaitingUnknownInScope(
  decisions: IDecisionsSetSource,
  scope: IDecisionsScope,
): boolean {
  return decisionsSetMatchesScope(decisions, scope) && decisions.unknown;
}

/**
 * F-0530 (AC-19.8): a value that changes whenever this web view's set of waiting decisions does —
 * an answer, a take-back, a new one — for a reader that must read the everywhere set again then.
 * A trigger, never a number to show: the count's one owner stays here (I-0611).
 */
export function decisionsSetChanged(decisions: IDecisionsSetSource): unknown {
  return decisions.waitingCount;
}
